import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const SIGN_TTL_SECONDS = 3600;
// Refresh a bit before the signed URL actually expires so we never hand out
// a stale link that would 403 in <img>.
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

type CacheEntry = { url: string; expiresAt: number };
const cache = new Map<string, CacheEntry>();

/**
 * Resolves a storage path to a signed URL. If `path` is already a full URL, returns it.
 *
 * Signed URLs expire after `SIGN_TTL_SECONDS`. We cache them in-memory but track
 * an expiry timestamp so old History rows don't render broken images after an
 * hour of browsing.
 */
export function useSignedImage(
  bucket: "user-photos" | "product-images" | "tryon-results",
  path: string | null | undefined,
): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!path) {
      setUrl(null);
      return;
    }
    if (/^https?:\/\//.test(path) || path.startsWith("data:")) {
      setUrl(path);
      return;
    }
    const key = `${bucket}/${path}`;
    const cached = cache.get(key);
    if (cached && cached.expiresAt > Date.now()) {
      setUrl(cached.url);
      return;
    }
    let active = true;
    supabase.storage
      .from(bucket)
      .createSignedUrl(path, SIGN_TTL_SECONDS)
      .then(({ data, error }) => {
        if (!active) return;
        if (error || !data) {
          // Drop a stale cache entry so the next render retries.
          cache.delete(key);
          return;
        }
        cache.set(key, {
          url: data.signedUrl,
          expiresAt: Date.now() + SIGN_TTL_SECONDS * 1000 - REFRESH_BUFFER_MS,
        });
        setUrl(data.signedUrl);
      });
    return () => {
      active = false;
    };
  }, [bucket, path]);

  return url;
}
